import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, Paperclip, Link2, X, Download, Trash2, ExternalLink, FileText, Image, File, Eye } from 'lucide-react';
import api from '../../lib/api';
import toast from 'react-hot-toast';

// ── Image Lightbox ──────────────────────────────────────────
function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'zoom-out',
    }}>
      <button onClick={onClose} style={{
        position: 'absolute', top: 16, right: 16,
        background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
        width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', color: '#fff',
      }}>
        <X size={20} />
      </button>
      <img
        src={src} alt={alt}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.4)', cursor: 'default' }}
      />
    </div>
  );
}

// ── File icon helper ────────────────────────────────────────
function FileIcon({ mime, filename, size = 16 }) {
  if (mime?.startsWith('image/')) return <Image size={size} style={{ color: 'var(--blue)', flexShrink: 0 }} />;
  if (mime?.includes('pdf')) return <FileText size={size} style={{ color: 'var(--red)', flexShrink: 0 }} />;
  if (mime?.includes('spreadsheet') || filename?.match(/\.(xlsx?|csv)$/i)) return <FileText size={size} style={{ color: 'var(--green)', flexShrink: 0 }} />;
  if (mime?.includes('document') || filename?.match(/\.(docx?|rtf)$/i)) return <FileText size={size} style={{ color: 'var(--blue)', flexShrink: 0 }} />;
  return <File size={size} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />;
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Attachment List ─────────────────────────────────────────
export function AttachmentList({ parentType, parentId, refresh }) {
  const [attachments, setAttachments] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [blobUrls, setBlobUrls] = useState({});

  const fetchAttachments = useCallback(() => {
    if (!parentType || !parentId) return;
    api.get(`/attachments?parent_type=${parentType}&parent_id=${parentId}`)
      .then(d => setAttachments(d.attachments || []))
      .catch(() => {});
  }, [parentType, parentId]);

  useEffect(() => { fetchAttachments(); }, [fetchAttachments, refresh]);

  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [blobUrls]);

  const handleDelete = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
      toast.success('Attachment deleted');
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  const handleDownload = async (att) => {
    try {
      const blob = await api.blob(`/attachments/${att.id}?download=1`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleView = async (att) => {
    if (att.storage_type === 'external') {
      window.open(att.external_url, '_blank', 'noopener');
      return;
    }

    const isImage = att.mime_type?.startsWith('image/');
    if (isImage) {
      // Use cached blob URL or fetch a new one
      if (blobUrls[att.id]) {
        setLightbox({ src: blobUrls[att.id], alt: att.filename });
        return;
      }
      try {
        const blob = await api.blob(`/attachments/${att.id}`);
        const url = URL.createObjectURL(blob);
        setBlobUrls(prev => ({ ...prev, [att.id]: url }));
        setLightbox({ src: url, alt: att.filename });
      } catch {
        toast.error('Failed to load image');
      }
    } else {
      handleDownload(att);
    }
  };

  if (attachments.length === 0) return null;

  const images = attachments.filter(a => a.storage_type === 'local' && a.mime_type?.startsWith('image/'));
  const files = attachments.filter(a => !(a.storage_type === 'local' && a.mime_type?.startsWith('image/')));

  return (
    <>
      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}

      <div style={{ marginTop: 8 }}>
        {/* Image thumbnails */}
        {images.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: files.length ? 8 : 0 }}>
            {images.map(att => (
              <ImageThumb key={att.id} att={att} blobUrls={blobUrls} setBlobUrls={setBlobUrls} onView={() => handleView(att)} onDelete={() => handleDelete(att)} onDownload={() => handleDownload(att)} />
            ))}
          </div>
        )}

        {/* File list */}
        {files.map(att => (
          <div key={att.id} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
            borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
            marginBottom: 4, fontSize: 12.5,
          }}>
            {att.storage_type === 'external' ? (
              <Link2 size={14} style={{ color: 'var(--blue)', flexShrink: 0 }} />
            ) : (
              <FileIcon mime={att.mime_type} filename={att.filename} size={14} />
            )}
            <button onClick={() => handleView(att)} style={{
              flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
              color: 'var(--text-primary)', fontSize: 12.5,
            }} title={att.filename}>
              {att.filename}
            </button>
            {att.size_bytes > 0 && (
              <span style={{ color: 'var(--text-muted)', fontSize: 11, flexShrink: 0 }}>{formatSize(att.size_bytes)}</span>
            )}
            {att.storage_type === 'external' && (
              <button onClick={() => handleView(att)} className="btn btn-ghost btn-sm" style={{ padding: '2px 4px' }} title="Open link">
                <ExternalLink size={12} />
              </button>
            )}
            {att.storage_type === 'local' && (
              <button onClick={() => handleDownload(att)} className="btn btn-ghost btn-sm" style={{ padding: '2px 4px' }} title="Download">
                <Download size={12} />
              </button>
            )}
            <button onClick={() => handleDelete(att)} className="btn btn-ghost btn-sm" style={{ padding: '2px 4px', color: 'var(--text-muted)' }} title="Delete">
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Image Thumbnail ─────────────────────────────────────────
function ImageThumb({ att, blobUrls, setBlobUrls, onView, onDelete, onDownload }) {
  const [thumbUrl, setThumbUrl] = useState(blobUrls[att.id] || null);

  useEffect(() => {
    if (blobUrls[att.id]) { setThumbUrl(blobUrls[att.id]); return; }
    let cancelled = false;
    api.blob(`/attachments/${att.id}`).then(blob => {
      if (cancelled) return;
      const url = URL.createObjectURL(blob);
      setThumbUrl(url);
      setBlobUrls(prev => ({ ...prev, [att.id]: url }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [att.id]);

  return (
    <div style={{ position: 'relative', width: 80, height: 80, borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer', flexShrink: 0 }}>
      {thumbUrl ? (
        <img src={thumbUrl} alt={att.filename} onClick={onView} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div onClick={onView} style={{ width: '100%', height: '100%', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Image size={20} style={{ color: 'var(--text-muted)' }} />
        </div>
      )}
      {/* Hover overlay */}
      <div className="thumb-overlay" style={{
        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        opacity: 0, transition: 'opacity 0.15s',
      }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
        onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
      >
        <button onClick={onView} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 4, padding: 4, cursor: 'pointer', color: '#fff' }} title="View">
          <Eye size={14} />
        </button>
        <button onClick={onDownload} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 4, padding: 4, cursor: 'pointer', color: '#fff' }} title="Download">
          <Download size={14} />
        </button>
        <button onClick={onDelete} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 4, padding: 4, cursor: 'pointer', color: '#fff' }} title="Delete">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ── File Upload Zone ────────────────────────────────────────
export function FileUploadZone({ parentType, parentId, onUploaded, compact = false }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files.length || !parentId) return;
    setUploading(true);
    let successCount = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('parent_type', parentType);
        formData.append('parent_id', parentId);
        await api.upload('/attachments', formData);
        successCount++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err.message}`);
      }
    }
    setUploading(false);
    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
      onUploaded?.();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragging(false); };

  const handleLinkSubmit = async () => {
    if (!linkUrl.trim() || !parentId) return;
    try {
      await api.post('/attachments/link', {
        parent_type: parentType,
        parent_id: parentId,
        url: linkUrl.trim(),
        name: linkName.trim() || undefined,
      });
      toast.success('Link added');
      setLinkUrl('');
      setLinkName('');
      setShowLinkForm(false);
      onUploaded?.();
    } catch (err) {
      toast.error(err.message || 'Failed to add link');
    }
  };

  if (compact) {
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <input type="file" ref={fileInputRef} multiple style={{ display: 'none' }}
          onChange={(e) => { handleFiles(Array.from(e.target.files)); e.target.value = ''; }}
        />
        <button className="btn btn-ghost btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading || !parentId}
          title="Attach file" style={{ padding: '4px 6px' }}>
          <Paperclip size={14} /> {uploading ? 'Uploading…' : ''}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowLinkForm(s => !s)} disabled={!parentId}
          title="Add external link" style={{ padding: '4px 6px' }}>
          <Link2 size={14} />
        </button>
        {showLinkForm && (
          <div style={{
            position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
            background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            padding: 12, boxShadow: 'var(--shadow)', zIndex: 10, width: 320,
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Add External Link</div>
            <input className="form-input" placeholder="URL *" value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)} style={{ fontSize: 12, padding: '5px 8px', marginBottom: 6 }} />
            <input className="form-input" placeholder="Display name (optional)" value={linkName}
              onChange={e => setLinkName(e.target.value)} style={{ fontSize: 12, padding: '5px 8px', marginBottom: 8 }} />
            <div className="flex gap-2">
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowLinkForm(false); setLinkUrl(''); setLinkName(''); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleLinkSubmit} disabled={!linkUrl.trim()}>Add Link</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <input type="file" ref={fileInputRef} multiple style={{ display: 'none' }}
        onChange={(e) => { handleFiles(Array.from(e.target.files)); e.target.value = ''; }}
      />

      <div
        onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
        onClick={() => !uploading && fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)', padding: '16px 20px',
          textAlign: 'center', cursor: uploading ? 'default' : 'pointer',
          background: dragging ? 'var(--accent-light)' : 'var(--bg-secondary)',
          transition: 'all 0.15s',
        }}
      >
        {uploading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
            <div className="spinner" style={{ width: 16, height: 16 }} /> Uploading…
          </div>
        ) : (
          <>
            <Upload size={20} style={{ color: 'var(--text-muted)', marginBottom: 6 }} />
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Drop files here or <span style={{ color: 'var(--accent)', fontWeight: 500 }}>browse</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Max 10MB per file</div>
          </>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowLinkForm(s => !s)} style={{ fontSize: 12, padding: '4px 8px' }}>
          <Link2 size={13} /> Add external link
        </button>
      </div>

      {showLinkForm && (
        <div style={{ marginTop: 8, padding: 12, background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>External Link (OneDrive, Google Drive, etc.)</div>
          <input className="form-input" placeholder="URL *" value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)} style={{ fontSize: 12.5, padding: '6px 10px', marginBottom: 6 }} />
          <input className="form-input" placeholder="Display name (optional)" value={linkName}
            onChange={e => setLinkName(e.target.value)} style={{ fontSize: 12.5, padding: '6px 10px', marginBottom: 8 }} />
          <div className="flex gap-2">
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowLinkForm(false); setLinkUrl(''); setLinkName(''); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleLinkSubmit} disabled={!linkUrl.trim()}>Add Link</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Attachments Panel (combined zone + list for sidebars) ───
export default function AttachmentsPanel({ parentType, parentId }) {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="card card-sm">
      <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 12, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        <Paperclip size={12} style={{ display: 'inline', marginRight: 5 }} />Attachments
      </div>
      <FileUploadZone parentType={parentType} parentId={parentId} onUploaded={() => setRefreshKey(k => k + 1)} />
      <AttachmentList parentType={parentType} parentId={parentId} refresh={refreshKey} />
    </div>
  );
}
